import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { YoolTable, StatusPill, RowActions, IconButton } from '../../components/ui/YoolTable';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '../../components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area
} from 'recharts';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  RefreshCw,
  AlertCircle,
  Calendar,
  Mail,
  Phone,
  Search,
  ScrollText
} from 'lucide-react';
import contratantesService from '../../services/contratantesService';
import ContratanteForm from './ContratanteForm';
import TrazabilidadModal from './TrazabilidadModal';
import PlanesBadge from './PlanesBadge';

/**
 * Modos:
 *   view          Clientes → Contratantes. Consulta + informe de trazabilidad.
 *   manage        Clientes → Gestión de Contratantes. Edición.
 *   trazabilidad  Trazabilidad → Contratantes. Solo el informe: sin "Ver" ni
 *                 edición, para que la sección quede acotada a su propósito.
 */
const ContratantesList = ({ mode = 'view' }) => {
  const showTrazabilidad = mode === 'view' || mode === 'trazabilidad';
  const showVer          = mode === 'view';
  const showEditar       = mode === 'manage';
  // En Trazabilidad la pantalla es solo la tabla: sin KPIs, gráficos ni la Card
  // de filtros (YoolTable ya trae buscador propio). El foco es encontrar al
  // contratante y abrir su informe.
  const showResumen      = mode !== 'trazabilidad';

  const [contratantes, setContratantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContratante, setSelectedContratante] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFormReadOnly, setIsFormReadOnly] = useState(false);
  const [trazabilidadId, setTrazabilidadId] = useState(null);
  const [trazabilidadNombre, setTrazabilidadNombre] = useState('');



  useEffect(() => {
    loadContratantes();
  }, [statusFilter, searchTerm]);

  const loadContratantes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await contratantesService.getContrataciones({
        estado: statusFilter === 'todos' ? null : statusFilter,
        search: searchTerm,
        soloConSuscripcion: true,
      });
      setContratantes(response.data || []);
      
    } catch (err) {
      console.error('Error al cargar contratantes:', err);
      setError('Error al cargar los contratantes. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Datos para gráficos con configuración mejorada de Shadcn UI v4
  const chartData = useMemo(() => {
    if (!contratantes.length) return { barData: [], lineData: [], chartConfig: {} };

    // Datos para gráfico de barras - Estados
    const estadosCount = contratantes.reduce((acc, contratante) => {
      acc[contratante.estado] = (acc[contratante.estado] || 0) + 1;
      return acc;
    }, {});

    const barData = Object.entries(estadosCount).map(([estado, count]) => ({
      estado: estado.charAt(0).toUpperCase() + estado.slice(1),
      cantidad: count,
      fill: estado === 'aprobada' ? 'var(--chart-2)' : 
            estado === 'pendiente' ? 'var(--chart-3)' : 
            estado === 'rechazada' ? 'var(--chart-4)' : 'var(--chart-5)'
    }));

    // Datos para gráfico de línea - Contrataciones por día
    const contratacionesPorDia = contratantes.reduce((acc, contratante) => {
      const fecha = new Date(contratante.created_at).toLocaleDateString('es-CL');
      acc[fecha] = (acc[fecha] || 0) + 1;
      return acc;
    }, {});

    const lineData = Object.entries(contratacionesPorDia)
      .sort(([a], [b]) => new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-')))
      .map(([fecha, count]) => ({
        fecha,
        contrataciones: count
      }));

    // Configuración de colores para los gráficos siguiendo Shadcn UI v4
    const chartConfig = {
      cantidad: {
        label: "Cantidad",
        color: "var(--chart-1)",
      },
      aprobada: {
        label: "Aprobada",
        color: "var(--chart-2)",
      },
      pendiente: {
        label: "Pendiente", 
        color: "var(--chart-3)",
      },
      rechazada: {
        label: "Rechazada",
        color: "var(--chart-4)",
      },
      contrataciones: {
        label: "Contrataciones",
        color: "var(--chart-1)",
      },
    };

    return { barData, lineData, chartConfig };
  }, [contratantes]);

  // Filtrar contratantes
  const filteredContratantes = useMemo(() => {
    return contratantes.filter(contratante => {
      const matchesSearch = !searchTerm || 
        contratante.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contratante.rut.includes(searchTerm) ||
        contratante.mail.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = !statusFilter || contratante.estado === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [contratantes, searchTerm, statusFilter]);

  // Estadísticas
  const stats = useMemo(() => {
    const total = contratantes.length;
    const aprobadas = contratantes.filter(c => c.estado === 'aprobada').length;
    const pendientes = contratantes.filter(c => c.estado === 'pendiente').length;
    const rechazadas = contratantes.filter(c => c.estado === 'rechazada').length;

    return { total, aprobadas, pendientes, rechazadas };
  }, [contratantes]);

  const columns = [
    {
      key: 'codigo_suscripcion',
      label: 'Código suscripción',
      render: (r) => (
        <span className="font-mono text-sm">{r.codigo_suscripcion || r.codigo || '—'}</span>
      ),
    },
    {
      key: 'nombre',
      label: 'Nombre',
      render: (r) => (
        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span>{r.nombre}</span>
          {r.num_planes >= 1 && (
            <PlanesBadge
              contratacionId={r.id}
              numPlanes={r.num_planes}
              codigosPrecargados={r.certificados}
            />
          )}
        </div>
      ),
    },
    {
      key: 'rut',
      label: 'RUT',
      render: (r) => <span className="font-mono">{r.rut}</span>,
    },
    {
      key: 'mail',
      label: 'Email',
      render: (r) => (
        <div className="flex items-center space-x-2">
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="text-sm">{r.mail}</span>
        </div>
      ),
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      render: (r) => {
        if (!r.telefono) return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <div className="flex items-center space-x-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <a href={`tel:${r.telefono}`} className="text-sm hover:underline">{r.telefono}</a>
          </div>
        );
      },
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => <StatusPill estado={r.estado} />,
    },
    {
      key: 'created_at',
      label: 'Fecha Creación',
      render: (r) => (
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm">
            {new Date(r.created_at).toLocaleDateString('es-CL')}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      // En trazabilidad la columna tiene una sola acción: RowActions la alinea
      // a la derecha (pensado para 2-3 botones) y queda descolgada del header.
      align: mode === 'trazabilidad' ? 'center' : undefined,
      render: (r) => (
        <RowActions center={mode === 'trazabilidad'}>
          {showTrazabilidad && (
            <IconButton
              title="Ver trazabilidad CMF"
              onClick={() => {
                setTrazabilidadId(r.id);
                setTrazabilidadNombre(r.nombre || r.rut);
              }}
            >
              <ScrollText size={16} />
            </IconButton>
          )}
          {showVer && (
            <IconButton icon="eye" label="Ver" onClick={() => handleViewContratante(r)} />
          )}
          {showEditar && (
            <IconButton icon="edit" label="Editar" onClick={() => handleEditContratante(r)} />
          )}
        </RowActions>
      ),
    },
  ];

  const handleViewContratante = (contratante) => {
    setSelectedContratante(contratante);
    setIsFormReadOnly(true);
    setIsFormOpen(true);
  };

  const handleEditContratante = (contratante) => {
    setSelectedContratante(contratante);
    setIsFormReadOnly(false);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setSelectedContratante(null);
    setIsFormOpen(false);
    setIsFormReadOnly(false);
  };

  const handleSaveContratante = async (formData) => {
    try {
      if (selectedContratante) {
        // Actualizar contratante existente
        await contratantesService.updateContratante(selectedContratante.id, formData);
      } else {
        // Crear nuevo contratante
        await contratantesService.createContratante(formData);
      }
      
      // Recargar la lista
      await loadContratantes();
    } catch (err) {
      throw new Error(err.message || 'Error al guardar el contratante');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Cargando contratantes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Contratantes</h1>
          <p className="text-muted-foreground">
            {mode === 'trazabilidad'
              ? 'Busca un contratante para ver su informe de trazabilidad'
              : 'Gestión de contrataciones y seguimiento de estados'}
          </p>
        </div>
        {showEditar && (
          <Button onClick={() => { setIsFormReadOnly(false); setIsFormOpen(true); }}>
            <Users className="h-4 w-4 mr-2" />
            Nuevo Contratante
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {showResumen && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aprobadas</CardTitle>
            <UserCheck className="h-4 w-4" style={{ color: 'var(--chart-2)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: 'var(--chart-2)' }}>{stats.aprobadas}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <Clock className="h-4 w-4" style={{ color: 'var(--chart-3)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: 'var(--chart-3)' }}>{stats.pendientes}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rechazadas</CardTitle>
            <UserX className="h-4 w-4" style={{ color: 'var(--chart-4)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: 'var(--chart-4)' }}>{stats.rechazadas}</div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Charts */}
      {showResumen && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Estados */}
        <Card>
          <CardHeader>
            <CardTitle>Estados de Contrataciones</CardTitle>
            <CardDescription>Distribución por estado</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartData.chartConfig} className="min-h-[200px] w-full">
              <BarChart accessibilityLayer data={chartData.barData}>
                <CartesianGrid vertical={false} />
                <XAxis 
                  dataKey="estado"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                />
                <YAxis 
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => Number.isInteger(value) ? value.toString() : ''}
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                />
                <ChartTooltip 
                  cursor={false}
                  content={<ChartTooltipContent indicator="dashed" />}
                />
                <Bar dataKey="cantidad" radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Area Chart - Tendencia */}
        <Card>
          <CardHeader>
            <CardTitle>Tendencia de Contrataciones</CardTitle>
            <CardDescription>Contrataciones por día</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartData.chartConfig} className="min-h-[200px] w-full">
              <AreaChart accessibilityLayer data={chartData.lineData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="fecha"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => value.slice(0, 5)}
                />
                <YAxis 
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => Number.isInteger(value) ? value.toString() : ''}
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                />
                <ChartTooltip 
                  cursor={false}
                  content={<ChartTooltipContent indicator="dot" />}
                />
                <Area 
                  dataKey="contrataciones"
                  type="natural"
                  fill="var(--chart-1)"
                  fillOpacity={0.4}
                  stroke="var(--chart-1)"
                  stackId="a"
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Filters */}
      {showResumen && (
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, RUT o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos los estados</SelectItem>
                <SelectItem value="aprobada">Aprobada</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="rechazada">Rechazada</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadContratantes}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Data Table */}
      <YoolTable
        rows={filteredContratantes}
        columns={columns}
        // `codigo` va junto a `codigo_suscripcion` porque el servicio mapea el
        // código a `codigo_suscripcion` pero la columna aún hace fallback a
        // `codigo`: buscando por ambos no se escapa ninguna fila.
        search={{
          placeholder: 'Buscar por código, certificado, nombre, RUT o email…',
          keys: [
            'codigo_suscripcion', 'codigo', 'certificados_codigos',
            'nombre', 'rut', 'mail',
          ],
        }}
        toolbar={
          <span className="text-sm text-muted-foreground">
            Mostrando {filteredContratantes.length} de {contratantes.length} contratantes
          </span>
        }
      />

      {/* Form Dialog */}
      {isFormOpen && (
        <ContratanteForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          contratante={selectedContratante}
          onSave={handleSaveContratante}
          readOnly={isFormReadOnly}
        />
      )}

      {/* Trazabilidad CMF */}
      {showTrazabilidad && (
        <TrazabilidadModal
          open={!!trazabilidadId}
          contratanteId={trazabilidadId}
          nombreContratante={trazabilidadNombre}
          onClose={() => { setTrazabilidadId(null); setTrazabilidadNombre(''); }}
        />
      )}
    </div>
  );
};

export default ContratantesList;